import { useState } from "react";
import { problemData, ctaLink } from "../data/mock";
import {
  AlertTriangle,
  Building2,
  FileWarning,
  BadgeDollarSign,
  Timer,
  Home,
  Scale,
  Landmark,
  HandCoins,
  Ban,
  ChevronDown,
  Rocket,
  Globe,
  ArrowRight,
} from "lucide-react";

const founderIcons = [Building2, FileWarning, BadgeDollarSign, Timer];
const nriIcons = [Home, Scale, Landmark, HandCoins, Ban];

const ProblemCard = ({ heading, headingHighlight, summary, items, icons, accentIcon: AccentIcon, ctaText = "Book Your Compliance Diagnostic Call" }) => {
  const [open, setOpen] = useState(false);
  const headingParts = headingHighlight ? heading.split(headingHighlight) : [heading];

  return (
    <div className="bg-[#302f2c]/40 border border-[#3f4816]/15 rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-7 py-6 flex items-start gap-4 group"
      >
        <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-md bg-[#3f4816]/15 border border-[#3f4816]/25 flex items-center justify-center">
          <AccentIcon size={18} className="text-[#d9fb06]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg mb-1.5 leading-snug" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            {headingHighlight ? (
              <>
                <span className="text-[#888680] font-semibold">{headingParts[0]}</span>
                <span className="text-[#d9fb06] font-extrabold">{headingHighlight}</span>
                <span className="text-[#888680] font-semibold">{headingParts[1]}</span>
              </>
            ) : (
              <span className="text-[#dfddd6] font-bold">{heading}</span>
            )}
          </h3>
          <p className="text-[#888680]/70 text-sm leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            {summary}
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`text-[#888680] flex-shrink-0 mt-2 transition-transform duration-300 ${open ? "rotate-180 text-[#d9fb06]" : ""}`}
        />
      </button>

      <div className="transition-all duration-300 overflow-hidden" style={{ maxHeight: open ? "600px" : "0", opacity: open ? 1 : 0 }}>
        <div className="px-7 pb-6 pt-1">
          <div className="ml-14 flex flex-col gap-3">
            {items.map((item, i) => {
              const Icon = icons[i];
              const parts = item.text.split(item.keyword);
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded bg-[#3f4816]/10 flex items-center justify-center">
                    <Icon size={12} className="text-[#d9fb06]/70" />
                  </div>
                  <p className="text-[#888680] text-sm leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                    {parts[0]}<span className="text-[#dfddd6] font-medium">{item.keyword}</span>{parts[1]}
                  </p>
                </div>
              );
            })}
          </div>
          {/* CTA inside card */}
          <div className="ml-14 mt-5">
            <a
              href="#final-cta"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#final-cta")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] font-semibold text-xs px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] uppercase tracking-tight"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              {ctaText}
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProblemSection = () => {
  return (
    <section id="problem" className="relative py-28" style={{ background: "#1a1c1b" }}>
      <div className="max-w-[87.5rem] mx-auto px-10">
        <div className="mb-14">
          <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            The Problem
          </span>
          <h2 className="font-black uppercase leading-[0.88]" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2vw, 3.2rem)", letterSpacing: "-0.02em" }}>
            <span className="text-[#dfddd6]">Pick your situation. </span>
            <span className="text-[#d9fb06]">I've seen all of them.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-16">
          <ProblemCard
            heading="Founder going global?"
            headingHighlight="going global"
            summary="Setting up in Dubai but getting conflicting advice and worried about costly mistakes?"
            items={problemData.founderProblems.items}
            icons={founderIcons}
            accentIcon={Rocket}
            ctaText="Book Your Strategy Session"
          />
          <ProblemCard
            heading="NRI anywhere in the world?"
            headingHighlight="anywhere in the world"
            summary="Dealing with property sales, tax notices, or compliance gaps with advisors who don't get it?"
            items={problemData.nriProblems.items}
            icons={nriIcons}
            accentIcon={Globe}
          />
        </div>

        {/* Root Cause — visually bigger */}
        <div className="relative bg-[#302f2c]/40 border border-[#3f4816]/20 p-8 md:p-10 rounded-sm max-w-4xl">
          <div className="flex items-start gap-5">
            <div className="flex-shrink-0 w-12 h-12 rounded-md bg-[#d9fb06]/10 border border-[#d9fb06]/20 flex items-center justify-center">
              <AlertTriangle size={22} className="text-[#d9fb06]" />
            </div>
            <div>
              <p className="text-[#dfddd6] text-lg md:text-xl leading-relaxed font-medium" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                Your <span className="text-[#d9fb06] font-bold">Indian Tax Consultant</span> doesn't understand international tax. Your advisor abroad doesn't understand Indian law.
              </p>
              <p className="text-[#d9fb06] text-lg md:text-xl font-bold mt-2" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                You're paying two people who don't talk to each other.
              </p>
              <p className="text-[#dfddd6] text-base mt-1 font-semibold" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                That ends here.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
